export type JsonObject = Record<string, unknown>

export const isPlainObject = (v: unknown): v is JsonObject => typeof v === 'object' && v !== null && !Array.isArray(v)

export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => deepEqual(item, b[index]))
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const aKeys = Object.keys(a)
    const bKeys = Object.keys(b)
    return aKeys.length === bKeys.length && aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && deepEqual(a[key], b[key]))
  }
  return false
}

const clone = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(clone)
  }
  if (isPlainObject(value)) {
    const result: JsonObject = {}
    for (const [key, entry] of Object.entries(value)) {
      result[key] = clone(entry)
    }
    return result
  }
  return value
}

export const pruneUndefined = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => (item === undefined ? null : pruneUndefined(item)))
  }
  if (isPlainObject(value)) {
    const result: JsonObject = {}
    for (const [key, entry] of Object.entries(value)) {
      if (entry === undefined) {
        continue
      }
      result[key] = pruneUndefined(entry)
    }
    return result
  }
  return value
}

export const applyMergePatch = (target: unknown, patch: unknown): unknown => {
  if (!isPlainObject(patch)) {
    return clone(patch)
  }
  const result: JsonObject = isPlainObject(target) ? (clone(target) as JsonObject) : {}
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) {
      delete result[key]
    } else {
      result[key] = applyMergePatch(result[key], value)
    }
  }
  return result
}

const diff = (base: JsonObject, edited: JsonObject): JsonObject => {
  const result: JsonObject = {}
  const keys = new Set([...Object.keys(base), ...Object.keys(edited)])
  for (const key of keys) {
    const inBase = Object.prototype.hasOwnProperty.call(base, key)
    const inEdited = Object.prototype.hasOwnProperty.call(edited, key)
    if (!inBase && inEdited) {
      result[key] = clone(edited[key])
      continue
    }
    if (inBase && !inEdited) {
      result[key] = null
      continue
    }
    const baseValue = base[key]
    const editedValue = edited[key]
    if (isPlainObject(baseValue) && isPlainObject(editedValue)) {
      const nested = diff(baseValue, editedValue)
      if (Object.keys(nested).length > 0) {
        result[key] = nested
      }
      continue
    }
    if (!deepEqual(baseValue, editedValue)) {
      result[key] = clone(editedValue)
    }
  }
  return result
}

export const buildMergePatch = (base: unknown, edited: unknown): JsonObject => {
  const prunedBase = pruneUndefined(base)
  const prunedEdited = pruneUndefined(edited)
  return diff(isPlainObject(prunedBase) ? prunedBase : {}, isPlainObject(prunedEdited) ? prunedEdited : {})
}

export const defaultsFromSchema = (schema: unknown): JsonObject => {
  const defaults: JsonObject = {}
  if (!isPlainObject(schema) || !isPlainObject(schema.properties)) {
    return defaults
  }
  for (const [name, prop] of Object.entries(schema.properties)) {
    if (!isPlainObject(prop)) {
      continue
    }
    if ('default' in prop) {
      defaults[name] = clone(prop.default)
    } else if (prop.type === 'object' && isPlainObject(prop.properties)) {
      const nested = defaultsFromSchema(prop)
      if (Object.keys(nested).length > 0) {
        defaults[name] = nested
      }
    }
  }
  return defaults
}
